import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { NotificationPanelService } from './notifications-panel.service';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NotificationsHeaderComponent } from './notifications-header/notifications-header.component';
import { NotificationsPanel } from './notifications-panel.model';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { HelperService } from 'app/shared/services/helper.service';
import { ImpersonateService } from 'app/profile/impersonate-user/impersonate.service';

@Component({
  selector: 'app-notifications-panel',
  standalone: true,
  imports: [FormsModule, CommonModule, NotificationsHeaderComponent, TranslateModule],
  templateUrl: './notifications-panel.component.html',
  styleUrl: './notifications-panel.component.scss'
})

export class NotificationsPanelComponent implements OnInit, OnDestroy {
  @Input() isOpen = false;
  notifications: NotificationsPanel[] = [];
  viewUnreadOnly = false;
  @Output() close = new EventEmitter<void>();
  private destroy$ = new Subject<void>();

  constructor(
    private notificationService: NotificationPanelService,
    private router: Router,
    private impersonateService: ImpersonateService,
    public helperService: HelperService
  ) { }

  ngOnInit(): void {
    this.notificationService.notifications$
      .pipe(takeUntil(this.destroy$))
      .subscribe((notifications) => {
        this.notifications = notifications;
      });

    this.fetchNotifications();
  }

  fetchNotifications(): void {
    const params = { SkipCount: 0, TakeCount: 10 };
    this.notificationService.fetchNotifications(params);
  }

  closePanel(): void {
    this.isOpen = false;
    this.close.emit();
  }

  handleViewUnreadOnlyChange(value: boolean): void {
    this.viewUnreadOnly = value;
  }

  get filteredNotifications(): NotificationsPanel[] {
    return this.viewUnreadOnly
      ? this.notifications.filter((n) => n.status === 'New')
      : this.notifications;
  }

  onNotificationClick(notification: NotificationsPanel): void {
    if (notification.status === 'New' && !this.impersonateService.impersonatedUser) {
      this.notificationService.markNotificationAsRead(notification.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.notifications = this.notifications.map((n) =>
            n.id === notification.id ? { ...n, status: 'Read' } : n
          );
          this.closeNotificationsPanel();
          this.navigateToCase(notification.referenceId);
        });
    } else {
      this.closeNotificationsPanel();
      this.navigateToCase(notification.referenceId);
    }
  }

  closeNotificationsPanel(): void {
    this.isOpen = false;
    this.close.emit();
  }

  navigateToCase(referenceId: number): void {
    this.router.navigate(['/case-form', referenceId]);
  }

  markNotificationAsRead(id: number): void {
    this.notificationService.markNotificationAsRead(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.notifications = this.notifications.map((notification) =>
          notification.id === id ? { ...notification, status: 'Read' } : notification
        );
      });
  }

  markAllNotificationsAsRead(): void {
    this.notificationService.markAllNotificationsAsRead()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.notifications = this.notifications.map((notification) => ({
          ...notification,
          status: 'Read',
        }));
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
